import { StoreScheduleResponse } from '@ydistri/api-sdk';
import { Row, YColumnsType } from '@ydistri/ds';
import { computeRemSize } from '@ydistri/theme';
import React from 'react';
import ArchivedEntityListIcon from '../../../components/domain/EntityLists/ArchivedEntityListIcon';
import MultiActiveWarning from './MultiActiveWarning';
import { formatDateTime } from '@ydistri/generic-utils';
import {
  EntityColumns,
  EntityTableColumnProvider,
} from '../../../hooks/useEntityTableColumnsProvider';
import { createdAtSorter, formatPeriod, getActiveSiblings } from './storesScheduleLib';

interface ColumnFactoryOptions {
  allRecords: StoreScheduleResponse[];
  renderActions: (record: StoreScheduleResponse) => React.ReactNode;
  storeColumnsProvider: EntityTableColumnProvider;
}

const identifierColumn = { width: computeRemSize(120), showSorterTooltip: false };

export const getStoreScheduleColumns = ({
  allRecords,
  renderActions,
  storeColumnsProvider,
}: ColumnFactoryOptions): YColumnsType<StoreScheduleResponse>[] => [
  ...storeColumnsProvider<StoreScheduleResponse>({
    [EntityColumns.ID]: identifierColumn,
    [EntityColumns.CODE]: identifierColumn,
    [EntityColumns.NAME]: {
      ellipsis: true,
      showSorterTooltip: false,
      render: (_: string, record: StoreScheduleResponse) => (
        <Row $gap="0.5rem" $alignItems="center">
          <MultiActiveWarning siblings={getActiveSiblings(record, allRecords)}>
            {record.store.name}
          </MultiActiveWarning>
          {record.isArchived && <ArchivedEntityListIcon content="This closure is archived." />}
        </Row>
      ),
    },
  }),
  {
    title: 'Cannot send',
    key: 'cannotSend',
    width: computeRemSize(220),
    render: (record: StoreScheduleResponse) =>
      formatPeriod(record.cannotSendFrom, record.cannotSendTo),
  },
  {
    title: 'Cannot receive',
    key: 'cannotReceive',
    width: computeRemSize(220),
    render: (record: StoreScheduleResponse) =>
      formatPeriod(record.cannotReceiveFrom, record.cannotReceiveTo),
  },
  {
    title: 'Note',
    dataIndex: 'note',
    key: 'note',
    ellipsis: true,
  },
  {
    title: 'Created',
    key: 'createdAt',
    width: computeRemSize(180),
    showSorterTooltip: false,
    sorter: createdAtSorter,
    render: (record: StoreScheduleResponse) => formatDateTime(record.createdAt),
  },
  {
    title: '',
    key: 'actions',
    width: computeRemSize(100),
    align: 'right',
    render: (record: StoreScheduleResponse) => renderActions(record),
  },
];
